import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, AdminHeader } from "../../shared/components";
import { Button } from "../../shared/components";
import {
  Users,
  Ticket,
  Calendar,
  Settings,
  Database,
  CreditCard,
  DollarSign,
  History,
  MessageCircle
} from "lucide-react";
import { RoleProtectedRoute } from "../../shared/components";

const AdminDashboard = ({ adminData, onLogout }) => {
  const navigate = useNavigate();
  
  const handleNavigateToUserManagement = () => {
    navigate('/admin/users');
  };

  const handleNavigateToEventsManagement = () => {
    navigate('/admin/events');
  };

  const handleNavigateToReservationsManagement = () => {
    navigate('/admin/reservations');
  };

  const handleNavigateToStudentsManagement = () => {
    navigate('/admin/students');
  };

  const handleNavigateToTicketSales = () => {
    navigate('/admin/ticket-sales');
  };

  const handleNavigateToCashMovements = () => {
    navigate('/admin/cash-movements');
  };

  const handleNavigateToNotifications = () => {
    navigate('/admin/notifications');
  };

  const handleNavigateToAuditLogs = () => {
    navigate('/admin/audit-logs');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header consistente */}
      <AdminHeader
        title="Panel de Administración"
        adminData={adminData}
        onLogout={onLogout}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Título de la sección */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestión del Sistema
          </h1>
          <p className="text-gray-600">
            Administra todos los aspectos del sistema de reservas
          </p>
        </div>

        {/* Grid de módulos organizados */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          
          {/* Módulos de Administración (Solo Admin) */}
          <RoleProtectedRoute requiredRoles={['admin', 'user']}>
            <div className="md:col-span-2 xl:col-span-3">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-gray-600" />
                Administración del Sistema
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
              
              {/* Gestión de Usuarios - Solo Admin */}
              <RoleProtectedRoute requiredRole="admin">
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-red-500 hover:border-l-red-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-red-700 transition-colors">
                      <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                        <Users className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="text-base font-semibold">Usuarios</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Administra usuarios, roles y permisos
                    </p>
                    <Button
                      onClick={handleNavigateToUserManagement}
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2"
                    >
                      Gestionar
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Gestión de Eventos - Solo Admin */}
              <RoleProtectedRoute requiredRoles={['admin', 'user']}>
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500 hover:border-l-blue-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-blue-700 transition-colors">
                      <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-base font-semibold">Eventos</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Configura eventos y sus detalles
                    </p>
                    <Button
                      onClick={handleNavigateToEventsManagement}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2"
                    >
                      Gestionar
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Configuración del Sistema - Solo Admin */}
              <RoleProtectedRoute requiredRole="admin">
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-orange-500 hover:border-l-orange-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-orange-700 transition-colors">
                      <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                        <Database className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="text-base font-semibold">Configuración</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Parámetros del sistema
                    </p>
                    <Button
                      onClick={() => navigate('/admin/config')}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2"
                    >
                      Configurar
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Historial de Movimientos - Solo Admin */}
              <RoleProtectedRoute requiredRole="admin">
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-slate-500 hover:border-l-slate-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-slate-700 transition-colors">
                      <div className="p-2 bg-slate-100 rounded-lg group-hover:bg-slate-200 transition-colors">
                        <History className="w-5 h-5 text-slate-600" />
                      </div>
                      <span className="text-base font-semibold">Historial de Movimientos</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Auditoría de entradas por alumno
                    </p>
                    <Button 
                      onClick={handleNavigateToAuditLogs}
                      className="w-full bg-slate-600 hover:bg-slate-700 text-white font-medium py-2"
                    >
                      Ver Historial
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Mensajes de WhatsApp - Solo Admin */}
              <RoleProtectedRoute requiredRole="admin">
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-emerald-500 hover:border-l-emerald-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-emerald-700 transition-colors">
                      <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                        <MessageCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-base font-semibold">Mensajes de WhatsApp</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Estado de envío y entrega de los mensajes
                    </p>
                    <Button 
                      onClick={handleNavigateToNotifications}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2"
                    >
                      Ver Mensajes
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>
            </div>
          </div>
          </RoleProtectedRoute>

          {/* Módulos de Operaciones (Admin y User) */}
          <div className="md:col-span-2 xl:col-span-3">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Ticket className="w-5 h-5 mr-2 text-gray-600" />
              Operaciones
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              
              {/* Gestionar Alumnos - Admin y User */}
              <RoleProtectedRoute requiredRoles={['admin', 'user']}>
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-green-500 hover:border-l-green-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-green-700 transition-colors">
                      <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-base font-semibold">Alumnos</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Importa listas de alumnos
                    </p>
                    <Button 
                      onClick={handleNavigateToStudentsManagement}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2"
                    >
                      Gestionar
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Gestionar Reservas - Admin y User */}
              <RoleProtectedRoute requiredRoles={['admin', 'user']}>
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-purple-500 hover:border-l-purple-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-purple-700 transition-colors">
                      <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                        <Ticket className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="text-base font-semibold">Reservas</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Revisa todas las reservas
                    </p>
                    <Button 
                      onClick={handleNavigateToReservationsManagement}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2"
                    >
                      Gestionar
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Venta de Entradas - Admin y User */}
              <RoleProtectedRoute requiredRoles={['admin', 'user']}>
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-indigo-500 hover:border-l-indigo-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-indigo-700 transition-colors">
                      <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                        <CreditCard className="w-5 h-5 text-indigo-600" />
                      </div>
                      <span className="text-base font-semibold">Venta de Entradas</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Proceso de venta de entradas
                    </p>
                    <Button 
                      onClick={handleNavigateToTicketSales}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2"
                    >
                      Vender
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>

              {/* Movimientos de Caja - Admin y User */}
              <RoleProtectedRoute requiredRoles={['admin', 'user']}>
                <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-emerald-500 hover:border-l-emerald-600 bg-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-gray-800 group-hover:text-emerald-700 transition-colors">
                      <div className="p-2 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="text-base font-semibold">Movimientos de Caja</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600 mb-4">
                      Control de transacciones
                    </p>
                    <Button 
                      onClick={handleNavigateToCashMovements}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2"
                    >
                      Ver Movimientos
                    </Button>
                  </CardContent>
                </Card>
              </RoleProtectedRoute>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard; 